import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';
import { fetchConfig } from '@theme/utilities';

class MiniCartComponent extends HTMLElement {
  #refreshTimeout;
  #pendingLines = new Set();
  #inputDebounces = new Map();

  connectedCallback() {
    if (this.dataset.initialized === 'true') return;

    this.dataset.initialized = 'true';
    this.cartUrl = this.dataset.cartUrl || '/cart';
    this.cartChangeUrl = this.dataset.cartChangeUrl || '/cart/change';
    this.cartTitle = this.dataset.cartTitle || 'Cart';
    this.subtotalLabel = this.dataset.cartSubtotalLabel || 'Subtotal';
    this.cartButtonLabel = this.dataset.cartButtonLabel || 'View cart';
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.locale = this.dataset.locale || document.documentElement.lang || 'en-US';

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('input', this.#handleInput);
    this.addEventListener('change', this.#handleChange);
    this.addEventListener('blur', this.#handleBlur, true);
    this.addEventListener('keydown', this.#handleKeydown);

    this.#syncHeaderCartCounts(this.#readInitialItemCount());
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('input', this.#handleInput);
    this.removeEventListener('change', this.#handleChange);
    this.removeEventListener('blur', this.#handleBlur, true);
    this.removeEventListener('keydown', this.#handleKeydown);
    window.clearTimeout(this.#refreshTimeout);
    this.#inputDebounces.forEach((timeoutId) => window.clearTimeout(timeoutId));
    this.#inputDebounces.clear();
  }

  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    window.clearTimeout(this.#refreshTimeout);
    this.#refreshTimeout = window.setTimeout(() => {
      this.refresh();
    }, 100);
  };

  #handleClick = (event) => {
    const button = event.target.closest('.qty-btn');
    if (!(button instanceof HTMLButtonElement)) return;

    const box = button.closest('.mini-cart-qty-box');
    const input = box?.querySelector('.qty-input');
    if (!(box instanceof HTMLElement) || !(input instanceof HTMLInputElement)) return;

    const currentValue = this.#sanitizeQuantity(input.value);
    const nextValue = button.classList.contains('plus') ? currentValue + 1 : Math.max(currentValue - 1, 0);

    input.value = String(nextValue);
    this.#queueQuantityUpdate(box, nextValue, false);
  };

  #handleInput = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.classList.contains('qty-input')) return;

    const box = input.closest('.mini-cart-qty-box');
    if (!(box instanceof HTMLElement)) return;

    const quantity = this.#sanitizeQuantity(input.value);
    input.value = String(quantity);
    this.#queueQuantityUpdate(box, quantity, true);
  };

  #handleChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.classList.contains('qty-input')) return;

    const box = input.closest('.mini-cart-qty-box');
    if (!(box instanceof HTMLElement)) return;

    this.#queueQuantityUpdate(box, this.#sanitizeQuantity(input.value), false);
  };

  #handleBlur = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.classList.contains('qty-input')) return;

    const box = input.closest('.mini-cart-qty-box');
    if (!(box instanceof HTMLElement)) return;

    this.#queueQuantityUpdate(box, this.#sanitizeQuantity(input.value), false);
  };

  #handleKeydown = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.classList.contains('qty-input')) return;

    if (event.key !== 'Enter') return;

    event.preventDefault();
    const box = input.closest('.mini-cart-qty-box');
    if (!(box instanceof HTMLElement)) return;

    this.#queueQuantityUpdate(box, this.#sanitizeQuantity(input.value), false);
  };

  #queueQuantityUpdate(box, quantity, debounce) {
    const line = box.dataset.line;
    const timeoutKey = `line-${line}`;
    const existingTimeout = this.#inputDebounces.get(timeoutKey);

    if (existingTimeout) {
      window.clearTimeout(existingTimeout);
      this.#inputDebounces.delete(timeoutKey);
    }

    const run = () => {
      this.#inputDebounces.delete(timeoutKey);
      this.#updateQuantity(box, quantity);
    };

    if (debounce) {
      const timeoutId = window.setTimeout(run, 500);
      this.#inputDebounces.set(timeoutKey, timeoutId);
      return;
    }

    run();
  }

  async #updateQuantity(box, quantity) {
    const line = Number(box.dataset.line || '0');
    const input = box.querySelector('.qty-input');
    if (!line || !(input instanceof HTMLInputElement)) return;

    const currentQuantity = Number(input.dataset.currentQuantity || input.defaultValue || '0');
    if (currentQuantity === quantity) return;
    if (this.#pendingLines.has(line)) return;

    const sections = this.#collectCartSections();
    const requestData = {
      line,
      quantity,
    };

    if (sections.length > 0) {
      requestData.sections = sections.join(',');
      requestData.sections_url = window.location.pathname;
    }

    const body = JSON.stringify(requestData);

    this.#pendingLines.add(line);
    box.dataset.loading = 'true';

    try {
      const response = await fetch(this.cartChangeUrl, fetchConfig('json', { body }));
      const responseText = await response.text();
      const parsedResponse = JSON.parse(responseText);

      if (!response.ok || parsedResponse.errors) {
        throw new Error(parsedResponse.errors || 'Mini cart update failed');
      }

      this.#render(parsedResponse);
      this.dispatchEvent(
        new CartUpdateEvent(parsedResponse, this.id || 'mini-cart', {
          itemCount: parsedResponse.item_count || 0,
          source: 'mini-cart-component',
          sections: parsedResponse.sections || {},
        })
      );
    } catch (error) {
      console.warn('Mini cart update failed:', error);
      await this.refresh();
    } finally {
      this.#pendingLines.delete(line);
      box.dataset.loading = 'false';
    }
  }

  async refresh() {
    try {
      const response = await fetch(this.#toJsonUrl(this.cartUrl), {
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Mini cart refresh failed with status ${response.status}`);
      }

      const cart = await response.json();
      this.#render(cart);
    } catch (error) {
      console.warn('Mini cart refresh failed:', error);
    }
  }

  #render(cart) {
    const itemCount = Number(cart.item_count || 0);

    this.dataset.currencyCode = cart.currency || this.dataset.currencyCode || '';
    this.hidden = itemCount === 0;
    this.setAttribute('aria-hidden', String(itemCount === 0));
    this.innerHTML = this.#buildMarkup(cart);
    this.#syncHeaderCartCounts(itemCount);
  }

  #buildMarkup(cart) {
    const currencyCode = cart.currency || this.dataset.currencyCode || '';
    const cartUrl = this.dataset.cartUrl || this.cartUrl;
    const itemsMarkup = (cart.items || [])
      .map((item, index) => this.#buildItemMarkup(item, index, currencyCode))
      .join('');

    return `
      <div class="mini-cart-header">
        <h4>${this.#escapeHtml(this.cartTitle)}</h4>
        <p>
          <span class="subtotal-title">${this.#escapeHtml(this.subtotalLabel)}</span><br>
          <span class="cart-subtotal">
            <span data-wpd-cart-total>${this.#escapeHtml(this.#formatCurrency(cart.total_price || 0, currencyCode))}</span>
          </span>
        </p>
        <a class="add-to-cart-btn" href="${this.#escapeAttribute(cartUrl)}">${this.#escapeHtml(this.cartButtonLabel)}</a>
      </div>
      <ul class="mini-cart-items">
        ${itemsMarkup}
      </ul>
    `;
  }

  #buildItemMarkup(item, index, currencyCode) {

    const imageMarkup = this.#buildImageMarkup(item);
    const line = index + 1;
    const productUrl = item.url || this.dataset.cartUrl || this.cartUrl;
    const price = this.#formatCurrency(item.final_line_price || 0, currencyCode);
    const decreaseLabel = this.#escapeHtml(this.dataset.decreaseQuantityLabel || 'Decrease quantity');
    const increaseLabel = this.#escapeHtml(this.dataset.increaseQuantityLabel || 'Increase quantity');
    const quantityLabel = this.#escapeHtml(this.dataset.quantityLabel || 'Quantity');
    const quantity = Number(item.quantity || 0);

    return `
      <li class="mini-cart-item" data-line="${line}">
        <div class="mini-cart-item-image">
          <a href="${this.#escapeAttribute(productUrl)}">
            ${imageMarkup}
          </a>
        </div>
        <div class="mini-cart-item-details">
          <div class="cart-item-price">
            <span
              data-wpd-cart-drawer-product-handle="${this.#escapeAttribute(item.handle || '')}"
              data-wpd-cart-drawer-variant-id="${this.#escapeAttribute(item.variant_id || '')}"
              data-wpd-cart-drawer-product-id="${this.#escapeAttribute(item.product_id || item.id || '')}"
              data-wpd-cart-drawer-line-price
              data-wpd-cart-drawer-item="${this.#escapeAttribute(item.key || '')}"
              data-wpd-cart-drawer-quantity="${this.#escapeAttribute(quantity)}"
            >
              ${this.#escapeHtml(price)}
            </span>
          </div>
          <div class="mini-cart-qty-box" data-line="${line}" data-loading="false">
            <button type="button" class="qty-btn minus" aria-label="${decreaseLabel}">-</button>
            <input
              class="qty-input"
              type="text"
              value="${quantity}"
              data-current-quantity="${quantity}"
              inputmode="numeric"
              pattern="[0-9]*"
              aria-label="${quantityLabel}"
            >
            <button type="button" class="qty-btn plus" aria-label="${increaseLabel}">+</button>
          </div>
        </div>
      </li>
    `;
  }

  #buildImageMarkup(item) {
    const featuredImage = item.featured_image || {};
    const rawUrl = featuredImage.url || item.image || '';
    if (!rawUrl) return this.dataset.placeholderImageMarkup || '';

    const originalWidth = Number(featuredImage.width || 600);
    const originalHeight = Number(featuredImage.height || 600);
    const renderedWidth = Math.min(originalWidth, 600);
    const renderedHeight = Math.max(1, Math.round((originalHeight / originalWidth) * renderedWidth));
    const imageUrl = this.#appendImageWidth(rawUrl, 600);
    const alt = featuredImage.alt || item.product_title || item.title || '';

    return `<img src="${this.#escapeAttribute(imageUrl)}" alt="${this.#escapeAttribute(alt)}" width="${renderedWidth}" height="${renderedHeight}" loading="lazy">`;
  }

  #appendImageWidth(url, width) {
    try {
      const parsedUrl = new URL(url, window.location.origin);
      parsedUrl.searchParams.set('width', String(width));
      return parsedUrl.toString();
    } catch (_) {
      return url;
    }
  }

  #formatCurrency(amount, currencyCode) {
    const normalizedCurrency = currencyCode || this.dataset.currencyCode || 'USD';
    return formatMoney(Number(amount || 0), this.moneyFormat, normalizedCurrency);
  }

  #collectCartSections() {
    const sections = new Set();

    document.querySelectorAll('cart-items-component').forEach((component) => {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sections.add(component.dataset.sectionId);
      }
    });

    return Array.from(sections);
  }

  #syncHeaderCartCounts(itemCount) {
    document.querySelectorAll('.header-desktop-cart-count').forEach((element) => {
      element.textContent = String(itemCount);
      element.setAttribute('aria-label', `${itemCount} items in cart`);
    });
  }

  #readInitialItemCount() {
    const countElement = this.querySelector('.qty-input');

    if (this.hidden) return 0;
    if (countElement instanceof HTMLInputElement) {
      return Array.from(this.querySelectorAll('.qty-input')).reduce((total, input) => {
        return total + Number(input.value || 0);
      }, 0);
    }

    return 0;
  }

  #sanitizeQuantity(value) {
    const numericValue = Number.parseInt(String(value).replace(/\D/g, ''), 10);

    if (Number.isNaN(numericValue) || numericValue < 0) {
      return 0;
    }

    return Math.min(numericValue, 999);
  }

  #toJsonUrl(url) {
    return url.endsWith('.js') ? url : `${url}.js`;
  }

  #escapeHtml(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  #escapeAttribute(value) {
    return this.#escapeHtml(value);
  }
}

if (!customElements.get('mini-cart-component')) {
  customElements.define('mini-cart-component', MiniCartComponent);
}
